import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  take,
  switchMap,
  retryWhen,
  share,
  map,
} from 'rxjs/operators';
import { LoginAlertComponent } from 'src/app/components/login-alert/login-alert.component';
import { createModalOptions } from 'src/app/_helpers/create-modal-options';
import {
  TmxTidConfig,
  TmxTidEvent,
  TmxTidModule,
  TmxTidService,
} from 'src/app/auth/models/tmx-tid-3.0.2';
import { environment } from 'src/environments/environment';
import { AccountDisplayName } from '../models/account';
import { AuthenticationService } from './authentication.service';
import { BrazeService } from 'src/app/services/braze.service';
import { ModalboxComponent } from 'src/app/components/modalbox/modalbox.component';
import { SubscriptionLevel, UserInfo } from '../models/user';
import { Token } from 'src/app/_models/authws/authws';
import {
  checkCreditCardStatus,
  getCreditExpiresDate,
} from 'src/app/_helpers/check-credit-card-expiration';
import { AuthURLConfig } from '../helpers/auth-url-config';
import { UserService } from 'src/app/services/user.service';
declare let tmxtidmodule: TmxTidModule;
@Injectable({
  providedIn: 'root',
})
export class TidService {
  userDisabledErrorMsg = 'Failed to Login.Your user or account is disabled.';
  private authorizeUrl = `${environment.trimbleCloudUrl}authorize`;

  isLoading = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading.asObservable();
  TID_TOKEN_KEY = 'tidToken';
  AUTH_CODE = 'authCode';
  isReload = false;
  errorMessage = new BehaviorSubject<string | null>(null);
  readonly errorMessage$ = this.errorMessage.asObservable();

  readonly accounts$ = new BehaviorSubject<AccountDisplayName[]>([]);

  readonly signInAccount$ = new BehaviorSubject<string>('');
  readonly signInUserName$ = new BehaviorSubject<string>('');

  returnUrl = this.route.snapshot.queryParams.returnUrl || '/';
  authParams = `?response_type=code&scope=openid ${environment.tidApplicationName}`;
  clientId = `&client_id=${environment.clientId}`;
  redirectUrl = `&redirect_uri=${location.origin}/tid/redirect`;
  currentUserInfo: UserInfo;
  setAuthCode(token: string) {
    localStorage.setItem(this.AUTH_CODE, token);
  }
  parentUrl: string;
  email: string;
  dialogRef: BsModalRef;

  removeAuthCode() {
    localStorage.removeItem(this.AUTH_CODE);
  }

  getAuthCode() {
    return localStorage.getItem(this.AUTH_CODE);
  }

  get tmxTidService(): TmxTidService | null {
    return tmxtidmodule.init(this.tidConfig);
  }
  get tidRedirectUrl() {
    return `${this.authorizeUrl}${this.authParams}${this.clientId}${this.redirectUrl}`;
  }

  constructor(
    private brazeService: BrazeService,
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private router: Router,
    private modalService: BsModalService,
    private route: ActivatedRoute,
    private userService: UserService
  ) {}
  readonly tidConfig: TmxTidConfig = {
    eventHandler: (event) => {
      this.getTidEventHandler(event);
    },
  };

  setIdToken(token: string) {
    localStorage.setItem(this.TID_TOKEN_KEY, token);
  }

  removeIdToken() {
    localStorage.removeItem(this.TID_TOKEN_KEY);
  }

  getIdToken() {
    return localStorage.getItem(this.TID_TOKEN_KEY);
  }
  readonly checkForComponents$ = timer(0, 100).pipe(
    switchMap(() => {
      const migrateComponent = document.getElementById(
        'tmxtid-sign-in-component'
      );
      if (migrateComponent) {
        return of(true);
      }
      return throwError(true);
    }),
    retryWhen((error) => error),
    take(1),
    share()
  );
  getTidEventHandler(event: TmxTidEvent) {
    switch (event.type) {
      case 'SIGN_IN_USERNAME':
        this.errorMessage.next(null);
        this.signInUserName$.next(event.username);
        break;
      case 'SIGN_IN_ACCOUNT':
        this.signInAccount$.next(event.account);
        break;
      case 'SIGN_IN_CONFIRM_CANCEL':
        this.showSignInUsername();
        break;
      case 'SIGN_IN_TID':
        this.signInWithTid(event.username, true);
        break;
    }
  }
  destroy() {
    this.tmxTidService.destroy();
  }

  showSignInUsername() {
    this.tmxTidService.showSignInUsername();
  }

  checkMigrationStatus(email: string, parentUrl: string): Observable<any> {
    this.parentUrl = parentUrl;
    const v1baseUrl = environment.ssoBaseUrl.replace('/v2', '/v1');
    const url = `${v1baseUrl}/trimbleid/migration-status`;
    return this.http
      .post<{ exists: boolean; migrated: boolean }>(url, {
        email,
      })
      .pipe(
        map(({ exists, migrated }) => {
          localStorage.setItem('userExists', JSON.stringify(exists));
          if (this.parentUrl.includes('login')) {
            if (!exists) {
              this.showSignInUsername();
              this.tmxTidService.setSignInValues();
              this.errorMessage.next('USERNAME_DOES_NOT_EXIST');
              this.tmxTidService.stopSignInWaiting();
              return;
            }
            if (migrated) {
              this.signInWithTid(email);
            } else {
              this.tmxTidService.setSignInValues(email);
              this.tmxTidService.showSignInConfirm();
            }
          } else {
            if (!exists && !migrated) {
              this.signInWithTid(email, true);
            } else {
              this.showModalRedirect(email, migrated);
            }
          }

          return true;
        }),

        catchError((error) => {
          this.errorMessage.next(error?.message);
          return throwError(error);
        })
      );
  }

  loginWithTid(account?: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=UTF-8',
    });
    const options = { headers, withCredentials: true };
    const v1baseUrl = environment.ssoBaseUrl.replace('/v2', '/v1');
    const url = `${v1baseUrl}/trimbleid/authenticate`;
    const body = {
      authorizationCode: this.getAuthCode(),
      redirectUrl: `${location.origin}/tid/redirect`,
      applicationName: environment.applicationName,
      account,
      trimbleApplication: environment.tidApplicationName,
    };
    return this.http.post(url, body, options).pipe(
      switchMap((user: any) => {
        if (user?.idToken) {
          this.setIdToken(user?.idToken);
        }
        if (user?.status === 'FailedToLoginAccountExpired') {
          this.signInAccount$.next('');
          user = user?.userInfo;
        }

        return this.authenticationService.getUserInfo(
          user?.username,
          user?.accountName,
          user?.authToken
        );
      }),
      catchError((error) => {
        this.router.navigate(['login']);
        return this.handleError(error);
      })
    );
  }

  loginSuccess(user: UserInfo, token: Token) {
    user._jwtToken = token;
    this.setUserInfobyAuthToken(user).then(
      (ssoResponse) => {
        localStorage.removeItem('logoutInProgress');
        this.brazeService.trackLogin(user);
        if (!location.href.includes('identity_provider=trimble_automation')) {
          this.authenticationService.setCurrentUser(user);
          this.updateLastLoginInformation(user);
        }
        this.authenticationService.authenticateWithRefreshToken();

        if (!user?.currentTermsOfUseAccepted) {
          // redirect to terms page
          this.router.navigate(['/terms']);
          return;
        }
        const expireDate = getCreditExpiresDate(user);
        const creditCardStatus = checkCreditCardStatus(user);
        const before = creditCardStatus?.hasCCExpireWarn
          ? creditCardStatus?.hasCCExpireWarn
          : false;
        const isExpired = creditCardStatus?.isCCExpired;

        if (
          ((before || isExpired || user?.hasBillingIssue) &&
            user?.isMonthlyPaidCustomer) ||
          user?.subscriptionLevel !== SubscriptionLevel.PREMIUM
        ) {
          //for expired premium users
          const value: any = {
            userInfo: {
              data: user,
              before: before,
              isExpired: isExpired,
              checkExpiry: expireDate,
            },
          };
          this.signInAccount$.next('');
          this.router.navigate(['/login']);
          this.modalService.show(
            LoginAlertComponent,
            createModalOptions(value)
          );
          return;
        }
        this.router.navigateByUrl(decodeURIComponent(this.returnUrl));
        this.accounts$.next([]);
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  updateLastLoginInformation(userInfo: UserInfo) {
    const userConfig = this.userService.getUserCfg();
    const hasTrackedLastLogin = Boolean(userConfig?.lastLogin);
    const lastLogin = [
      hasTrackedLastLogin ? userConfig?.lastLogin[1] : null,
      userInfo?.loggedInAt,
    ];
    this.userService.updateUserCfg({ hasTrackedLastLogin, lastLogin });
  }

  signInWithTid(email: string, promptCreate = false) {
    let url = `${this.tidRedirectUrl}&prompt=${
      promptCreate ? 'create' : 'login'
    }`;

    if (email) {
      url += `&login_hint=${encodeURIComponent(email)}`;
      url += `&email=${encodeURIComponent(email)}`;
    }
    if (this.parentUrl) {
      url += `&parentUrl=${encodeURIComponent(this.parentUrl)}`;
    }

    window.location.href = url;
  }
  showSignInAccounts(accounts: any) {
    this.tmxTidService.showSignInAccounts(accounts);
  }
  setUserInfobyAuthToken(user: UserInfo): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get<any>(
          `${AuthURLConfig.getApiKeybyAuthToken}?token=${user?.authToken}`
        )
        .subscribe((apiKeys) => {
          if (typeof apiKeys !== 'undefined' && apiKeys?.length) {
            this.http
              .post<any>(`${AuthURLConfig.getJwtToken}`, {
                apiKey: apiKeys[0].apiKey,
              })
              .subscribe((jwtToken) => {
                this.http
                  .get<any>(
                    `${AuthURLConfig.validateJwtToken}?key=${apiKeys[0].apiKey}&jtoken=${jwtToken.access_token}`
                  )
                  .subscribe((finalRes) => {
                    if (
                      (typeof finalRes === 'boolean' && finalRes) ||
                      (typeof finalRes === 'object' && finalRes.Result)
                    ) {
                      //set APIKey  and AuthToken
                      // TODO: set this region from "finalRes.Region" to access used authorised region
                      this.userService.setUserAPIKey(apiKeys[0].apiKey);
                      resolve(user);
                    } else {
                      this.handleError(
                        'You do not have access to this account. Please contact your administrator'
                      );
                    }
                  });
              });
          } else {
            resolve(apiKeys);
          }
        });
    });
  }

  private extractError(response: unknown) {
    if (response instanceof HttpErrorResponse) {
      const errorBody = (response?.error ? response?.error : {}) as Record<
        string,
        unknown
      >;
      return Array.isArray(errorBody.errors) ? errorBody.errors[0] : errorBody;
    }
  }
  private handleError(response: unknown) {
    const error = this.extractError(response);
    let accounts: AccountDisplayName[] = [];
    if (error?.status) {
      switch (error?.status) {
        case 'FailedToLoginDueToMultipleAccounts': {
          if (
            error &&
            Array.isArray(error?.accounts) &&
            error?.accounts.length > 0
          ) {
            this.isReload = true;
            accounts =
              error?.accounts.map((account: { accountName: string }) => ({
                displayName: account.accountName,
                value: account.accountName,
              })) || [];
          }
          this.accounts$.next(accounts);
          break;
        }

        case 'UserDisabled': {
          this.removeAuthCode();
          this.errorMessage.next(this.userDisabledErrorMsg);
          break;
        }

        case 'AccountDisabled': {
          this.removeAuthCode();
          this.errorMessage.next(this.userDisabledErrorMsg);
          break;
        }
        case 'FailedToLoginAccountExpired': {
          accounts = [];
          this.accounts$.next([]);
          break;
        }

        default: {
          this.removeAuthCode();
          const tidError = error?.reason ? error.reason : 'Failed to login.';
          this.errorMessage.next(tidError);
          if (tidError.includes('Failed to login.') && this.isReload) {
            window.location.reload();
            this.isReload = false;
          }
        }
      }
    } else {
      this.errorMessage.next('Failed to Login Account Expired');
    }
    this.tmxTidService.stopSignInWaiting();
    this.showSignInUsername();
    return throwError(error);
  }
  showModalRedirect(email: string, migrated: boolean) {
    let initialState;
    if (migrated) {
      initialState = {
        title: 'Trimble Identity (TID)',
        list: ['Email address found! Continue to Sign in.'],
        actionButtonText: 'Continue',
        okOnly: true,
        btnClass: 'btn btn-primary',
      };
    } else {
      initialState = {
        title: 'Trimble Identity (TID)',
        list: [
          'To access this account, you will need to complete your <a href="https://support.copilotpro.com/en/support/solutions/articles/19000135917-what-is-a-trimble-identity-tid-and-how-do-i-create-one-" target="_blank">Trimble Identity (TID)</a>.',
        ],
        actionButtonText: 'Complete Now',
        okOnly: true,
        btnClass: 'btn btn-primary',
      };
    }
    this.dialogRef = this.modalService?.show(ModalboxComponent, {
      initialState,
      backdrop: 'static',
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      class: 'modal-dialog-centered',
    });
    this.dialogRef.content.event.subscribe((v) => {
      this.signInWithTid(email, !migrated);
    });

    this.modalService.onHide.subscribe((val) => {
      this.isLoading.next(false);
    });
  }
}
